import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Download, FileText, Trash2 } from 'lucide-react';
import { api, ApiError, unduhBerkas } from '@/lib/api';
import { TemplateMode } from '@/components/jobs/TemplateMode';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, PageLoader, TableSkeleton } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

const KATEGORI = {
  eda: {
    judul: 'EDA',
    keterangan:
      'Menjelajah isi data: statistik, kelengkapan kolom, sebaran, dan korelasi. Hasilnya juga tersimpan sebagai JSON di Penyimpanan Pribadi, siap dibaca tahap Analytics.',
  },
  analytics: {
    judul: 'Analytics',
    keterangan:
      'Menyusun laporan dari data yang sudah dipahami. Bisa membaca hasil EDA sebagai masukan.',
  },
  // Machine Learning sengaja tidak ada di sini.
  //
  // Ia dipisahkan dari Analisa dan ditandai segera hadir di sidebar. Menaruh
  // kategori yang belum punya satu pun template hanya menghasilkan halaman
  // kosong yang terlihat seperti kerusakan, bukan seperti sesuatu yang belum
  // dibuat.
};

/** Satu bagian laporan, dirender sesuai tipenya. */
const Bagian = ({ bagian }) => {
  if (bagian.type === 'text') {
    return (
      <div>
        {bagian.title && (
          <h3 className="mb-1.5 text-sm font-bold text-[var(--text-primary)]">{bagian.title}</h3>
        )}
        <p className="text-sm whitespace-pre-wrap text-[var(--text-secondary)]">{bagian.body}</p>
      </div>
    );
  }

  if (bagian.type === 'metrics') {
    return (
      <div>
        {bagian.title && (
          <h3 className="mb-2 text-sm font-bold text-[var(--text-primary)]">{bagian.title}</h3>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {bagian.metrics.map((m) => (
            <div
              key={m.label}
              className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-4 py-3"
            >
              <p className="text-lg font-bold text-[var(--text-primary)]">{m.value}</p>
              <p className="text-xs text-[var(--text-muted)]">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (bagian.type === 'table') {
    return (
      <div>
        {bagian.title && (
          <h3 className="mb-2 text-sm font-bold text-[var(--text-primary)]">{bagian.title}</h3>
        )}
        <div className="max-h-96 overflow-auto rounded-lg border border-[var(--border-subtle)]">
          <TableWrapper>
            <Table>
              <thead>
                <Tr>
                  {bagian.columns.map((c, i) => (
                    <Th key={i}>{c}</Th>
                  ))}
                </Tr>
              </thead>
              <tbody>
                {bagian.rows.map((baris, i) => (
                  <Tr key={i}>
                    {baris.map((sel, j) => (
                      <Td key={j} className="max-w-64 truncate" title={sel}>
                        {sel}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </div>
      </div>
    );
  }

  if (bagian.type === 'chart') {
    return (
      <div>
        {bagian.title && (
          <h3 className="mb-2 text-sm font-bold text-[var(--text-primary)]">{bagian.title}</h3>
        )}
        {/* Grafik datang sebagai data URI PNG di dalam dokumen laporan, jadi
            tidak ada permintaan jaringan tambahan saat laporan dibuka. */}
        <img
          src={bagian.image}
          alt={bagian.title || 'Grafik hasil analisa'}
          className="max-w-full rounded-lg border border-[var(--border-subtle)] bg-white"
        />
      </div>
    );
  }

  return null;
};

const LaporanDialog = ({ id, onClose }) => {
  const toast = useToast();
  const [mengunduh, setMengunduh] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['analysis', id],
    queryFn: () => api.get(`/analyses/${id}`),
  });

  const laporan = data?.data;

  /**
   * Mengunduh laporan.
   *
   * HTML-nya berdiri sendiri: grafik tertanam sebagai data URI dan gayanya di
   * dalam berkas, jadi ia tetap utuh saat dipindahkan atau dilampirkan ke
   * surel. Untuk PDF, buka berkasnya lalu cetak - aturan cetaknya sudah
   * disiapkan di sisi server.
   */
  const unduh = async (format) => {
    setMengunduh(format);
    try {
      await unduhBerkas(`/analyses/${id}/unduh?format=${format}`, `laporan.${format}`);
    } catch (err) {
      toast.error('Gagal mengunduh laporan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi sebentar lagi.',
      });
    } finally {
      setMengunduh(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={laporan?.title ?? 'Laporan'}
      description={laporan?.summary}
      size="lg"
    >
      {isLoading || !laporan ? (
        <PageLoader label="Memuat laporan..." />
      ) : (
        <div className="space-y-6">
          {/* Unduhan ditaruh di atas, bukan di dasar dialog.

              Laporan bisa sangat panjang, dan tombol yang hanya bisa dicapai
              setelah menggulir seluruh isinya akan dianggap tidak ada oleh
              orang yang sekadar ingin menyimpannya. */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-4 py-3">
            <p className="text-xs text-[var(--text-muted)]">
              {laporan.sourceFileId?.filename
                ? `Sumber: ${laporan.sourceFileId.filename}`
                : 'Laporan hasil analisa'}
            </p>
            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={Download}
                loading={mengunduh === 'html'}
                onClick={() => unduh('html')}
              >
                Unduh HTML
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={mengunduh === 'json'}
                onClick={() => unduh('json')}
              >
                JSON
              </Button>
            </div>
          </div>
          {laporan.sections.map((bagian, i) => (
            <Bagian key={i} bagian={bagian} />
          ))}
        </div>
      )}
    </Modal>
  );
};

/**
 * Halaman Analisa: EDA, Analytics, dan Machine Learning dalam satu tempat.
 *
 * Ketiganya berbagi alur yang sama persis - pilih data, pilih analisa, isi
 * parameter, jalankan - jadi tiga menu terpisah hanya menambah klik tanpa
 * menambah kejelasan. Yang membedakan cuma kategori templatenya.
 */
export const AnalisaPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { kategori: dariUrl } = useParams();
  // Jalur yang tidak dikenal jatuh ke EDA daripada menampilkan layar kosong.
  const kategori = KATEGORI[dariUrl] ? dariUrl : 'eda';
  const meta = KATEGORI[kategori];
  const [membuka, setMembuka] = useState(null);
  const [hapusTarget, setHapusTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['analyses', kategori],
    queryFn: () => api.get('/analyses', { params: { category: kategori, limit: 20 } }),
    refetchInterval: 5000,
  });

  const hapus = useMutation({
    mutationFn: () => api.delete(`/analyses/${hapusTarget.id}`),
    onSuccess: (res) => {
      toast.success('Laporan dihapus', { description: res?.data?.message });
      setHapusTarget(null);
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
    },
    onError: (err) => {
      toast.error('Gagal menghapus', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      });
      setHapusTarget(null);
    },
  });

  const laporan = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">{meta.judul}</h1>
        <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">{meta.keterangan}</p>
      </div>

      <TemplateMode key={kategori} category={kategori} />

      <Card>
        <CardHeader
          title="Laporan Tersimpan"
          description="Hasil analisa yang pernah dijalankan di kategori ini."
        />
        {isLoading ? (
          <TableSkeleton rows={3} columns={4} />
        ) : laporan.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            compact
            title="Belum ada laporan"
            description="Jalankan salah satu analisa di atas; hasilnya akan muncul di sini."
          />
        ) : (
          <CardBody className="space-y-2">
            {laporan.map((l) => (
              <div
                key={l.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-3"
              >
                <FileText className="size-4 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => setMembuka(l.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
                    {l.title}
                  </p>
                  <p className="truncate text-xs text-[var(--text-muted)]">
                    {l.summary || 'Tanpa ringkasan'} · {formatRelative(l.createdAt)}
                  </p>
                </button>
                <Button variant="secondary" size="sm" onClick={() => setMembuka(l.id)}>
                  Buka
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  iconOnly
                  aria-label={`Hapus laporan ${l.title}`}
                  className="text-danger-500 hover:text-danger-600"
                  onClick={() => setHapusTarget(l)}
                />
              </div>
            ))}
          </CardBody>
        )}
      </Card>

      {membuka && <LaporanDialog id={membuka} onClose={() => setMembuka(null)} />}

      <ConfirmDialog
        open={Boolean(hapusTarget)}
        onClose={() => setHapusTarget(null)}
        onConfirm={() => hapus.mutate()}
        loading={hapus.isPending}
        destructive
        title={`Hapus laporan "${hapusTarget?.title}"?`}
        description="Laporan beserta grafiknya dihapus. Berkas sumbernya tidak disentuh."
        confirmLabel="Ya, hapus"
      />
    </div>
  );
};
